import { normalizeEmail } from '@helpers/normalizeEmail';
import { useAPI } from '@providers/useAPI';
import { useInitContext } from '@providers/InitProvider';
import { useQueryParams } from '@providers/useQueryParams';
import { useMutation } from '@tanstack/react-query';
import { createContext, ReactNode, useContext, useRef, useState } from 'react';
import { getSafeInternalRedirectPath } from '@helpers/getSafeInternalRedirectPath';
import type { LoginBodyDto } from 'app/API';
import { login as loginRequest } from '../api/login';
import type { TDesktopSignIn } from '../types';

type TState = {
  isLoading: boolean;
  email: string;
  setEmail: (v: string) => void;
  password: string;
  setPassword: (v: string) => void;
  error: string;
  clearError: () => void;
  login: () => Promise<void>;
};

const PageContext = createContext<TState>({
  isLoading: false,
  email: '',
  setEmail: () => null,
  password: '',
  setPassword: () => null,
  error: '',
  clearError: () => null,
  login: async () => undefined,
});

type TProps = {
  children: ReactNode;
  onSignedIn?: () => void;
  desktop?: TDesktopSignIn;
};

export const PageProvider = ({ children, onSignedIn, desktop }: TProps) => {
  const { call } = useAPI();
  const { authenticate } = useInitContext();
  const queryParams = useQueryParams();
  const [email, setEmail] = useState(() => normalizeEmail(desktop?.email || queryParams.get('email') || ''));
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const loginMutation = useMutation({
    mutationFn: (requestBody: LoginBodyDto) => loginRequest({ call, requestBody }),
  });
  const isLoginInFlight = useRef(false);

  const login = async () => {
    if (isLoginInFlight.current) {
      return;
    }
    setError('');
    if (!email || !password) {
      setError('Please enter your email and password');
      return;
    }
    isLoginInFlight.current = true;
    try {
      const data: unknown = await loginMutation.mutateAsync({
        email,
        password,
        product: queryParams.get('product') || '',
        ...(desktop ? { handoff: desktop.handoff } : {}),
      });
      if (!data) {
        setError('Something went wrong');
        return;
      }
      const errorCode =
        typeof data === 'object' && data !== null && 'code' in data && typeof data.code === 'string'
          ? data.code
          : undefined;
      const errorMessage =
        typeof data === 'object' && data !== null && 'message' in data && typeof data.message === 'string'
          ? data.message
          : undefined;
      if (errorCode || errorMessage) {
        setError(errorCode || errorMessage || 'Something went wrong');
        return;
      }
      if (onSignedIn) {
        onSignedIn();
        return;
      }
      authenticate(getSafeInternalRedirectPath(queryParams.get('goto')));
    } catch {
      setError('Something went wrong');
    } finally {
      isLoginInFlight.current = false;
    }
  };

  const clearError = () => {
    setError('');
  };

  return (
    <PageContext.Provider
      value={{
        isLoading: loginMutation.isPending,
        email,
        setEmail,
        password,
        setPassword,
        error,
        clearError,
        login,
      }}
    >
      {children}
    </PageContext.Provider>
  );
};

export const usePageContext = () => useContext(PageContext);
